import AIVisibilityDatePicker from "~/components/AIVisibilityDatePicker";
import BrandSelector from "~/components/BrandSelector";
import CountrySelector from "~/components/CountrySelector";
import ModelSelector from "~/components/ModelSelector";
import TagFilterSelector from "~/components/TagFilterSelector";
import { useAIVisibilityFilters } from "~/context/AIVisibilityFiltersContext";
import { useSubscription } from "~/context/SubscriptionContext";
import { useLocation } from "~/lib/team-router";
import { cn } from "~/lib/utils";

interface AIVisibilityFiltersProps {
  brandOptions: string[];
  brandColorMap: Record<string, string>;
  teamDomain: string;
  onBrandColorSaved: (domain: string, color: string) => void;
  className?: string;
}

export default function AIVisibilityFilters({
  brandOptions,
  brandColorMap,
  teamDomain,
  onBrandColorSaved,
  className,
}: AIVisibilityFiltersProps) {
  const location = useLocation();
  const {
    selectedBrands,
    setSelectedBrands,
    sourcesBrands,
    setSourcesBrands,
    llmId,
    setLlmId,
    countryCode,
    setCountryCode,
    answerCountries,
    sourcesLlmId,
    setSourcesLlmId,
    selectedTagIds,
    setSelectedTagIds,
    metricTab,
  } = useAIVisibilityFilters();
  const { subscription } = useSubscription();

  const isTrialing = subscription?.status === "trialing";
  const showTrialModeCharts = isTrialing;

  const isHomePage =
    location.pathname === "/ai-visibility/home" ||
    location.pathname.match(/\/ai-visibility\/prompts\/\d+/);
  const isSourcesPage = location.pathname.startsWith("/ai-visibility/sources");
  const isAITrafficPage =
    location.pathname === "/ai-visibility/ai-traffic-analytics";
  const isPromptsListPage = location.pathname === "/ai-visibility/prompts";
  const isFanoutsPage = location.pathname === "/ai-visibility/fanouts";
  const isTagsPage = location.pathname === "/ai-visibility/tags";

  const promptIdMatch = location.pathname.match(
    /\/ai-visibility\/prompts\/(\d+)/
  );
  const isSinglePromptPage = !!promptIdMatch;

  const isCitationDecayTab = isSourcesPage && metricTab === "decay";

  const showLlmSelector = isHomePage || isSourcesPage || isFanoutsPage;
  const showCountrySelector =
    (isHomePage || isSourcesPage || isFanoutsPage || isPromptsListPage) &&
    answerCountries.length > 1;
  const showBrandSelector = isHomePage || isSourcesPage;
  const showDatePicker =
    (isHomePage ||
    isSourcesPage ||
    isAITrafficPage ||
    isPromptsListPage ||
    isFanoutsPage) &&
    !isCitationDecayTab;
  const showTagFilter =
    (isHomePage || isSourcesPage) && !isSinglePromptPage && !isCitationDecayTab;

  if (isTagsPage) {
    return null;
  }

  if (
    !showTagFilter &&
    !showLlmSelector &&
    !showCountrySelector &&
    !showBrandSelector &&
    !showDatePicker
  ) {
    return null;
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {showTagFilter && (
        <TagFilterSelector
          selectedTagIds={selectedTagIds}
          onTagsChange={setSelectedTagIds}
          disabled={isSinglePromptPage}
        />
      )}

      {showLlmSelector && (
        <ModelSelector
          value={isSourcesPage ? sourcesLlmId : llmId}
          onValueChange={isSourcesPage ? setSourcesLlmId : setLlmId}
        />
      )}

      {showCountrySelector && (
        <CountrySelector
          value={countryCode}
          onValueChange={setCountryCode}
          countries={answerCountries}
        />
      )}

      {showBrandSelector && (
        <BrandSelector
          selectedBrands={isSourcesPage ? sourcesBrands : selectedBrands}
          brandOptions={brandOptions}
          brandColorMap={brandColorMap}
          teamDomain={teamDomain}
          onBrandColorSaved={onBrandColorSaved}
          onBrandsChange={isSourcesPage ? setSourcesBrands : setSelectedBrands}
        />
      )}

      {showDatePicker && (
        <AIVisibilityDatePicker
          showCompareToggle={!!isHomePage}
          hideOnTrialMode={true}
          showTrialModeCharts={showTrialModeCharts}
        />
      )}
    </div>
  );
}
